import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Theme } from '../lib/tokens';
import { FONTS } from '../lib/tokens';
import { Portrait } from '../components/Portrait';
import { Img } from '../components/Img';
import { Logo, Chip, Label } from '../components/common';
import { Icon } from '../components/icons';
import { useAuth } from '../hooks/useAuth';
import { ProfilePreview } from './ProfilePreview';
import { AdminPanel } from './AdminPanel';
import { getConsent, openConsentChoices, onConsentChange } from '../lib/analytics';
import { uploadPhoto, PhotoError } from '../lib/photos';
import { ETHNICITY_OPTIONS, COUNTRIES } from '../lib/identity';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';
const IS_DEV = import.meta.env.DEV;

interface MeScreenProps {
  t: Theme;
  dark: boolean;
  onToggleTheme: () => void;
}

function calcAge(dob: string): number {
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  if (today < new Date(today.getFullYear(), birth.getMonth(), birth.getDate())) age--;
  return age;
}

function calcCompleteness(profile: any, sparkAnswers: any[]): number {
  let score = 0;
  if (profile?.displayName) score += 20;
  if (profile?.bio) score += 15;
  if (profile?.photos?.length > 0) score += 25;
  if (profile?.activityPreferences) score += 15;
  if (profile?.locationPostcode) score += 10;
  if (sparkAnswers?.length >= 3) score += 15;
  return score;
}

export function MeScreen({ t, dark, onToggleTheme }: MeScreenProps) {
  const { userProfile, signOut, getIdToken, refreshProfile } = useAuth();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingSlot, setUploadingSlot] = useState<number | null>(null);
  const [deletingUrl, setDeletingUrl] = useState<string | null>(null);
  const [editingBio, setEditingBio] = useState(false);
  const [bioInput, setBioInput] = useState('');
  const [savingField, setSavingField] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const isAdmin = userProfile?.role === 'ADMIN';

  const saveField = async (field: string, value: any) => {
    setSavingField(field);
    const token = await getIdToken();
    try {
      await fetch(`${API_BASE}/api/auth/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [field]: value }),
      });
      await refreshProfile();
    } catch (e) {
      console.error(e);
    } finally {
      setSavingField(null);
    }
  };

  const profile = userProfile?.profile;
  const sparkAnswers = userProfile?.sparkAnswers || [];
  const age = profile?.dateOfBirth ? calcAge(profile.dateOfBirth) : null;
  const completeness = calcCompleteness(profile, sparkAnswers);
  const photos: string[] = (profile?.photos as string[]) || [];

  const handlePhotoSlotTap = (slotIndex: number) => {
    if (photos[slotIndex]) return; // slot occupied — handled by delete button
    setUploadingSlot(slotIndex);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || uploadingSlot === null) { setUploadingSlot(null); return; }

    const token = await getIdToken();

    try {
      // Resized, converted to JPEG and stripped of metadata (incl. GPS) first.
      const publicUrl = await uploadPhoto(file, token!);

      // Save URL to profile (insert at the tapped slot position)
      const newPhotos = [...photos];
      newPhotos.splice(uploadingSlot, 0, publicUrl);
      await fetch(`${API_BASE}/api/auth/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ photos: newPhotos.slice(0, 6) }),
      });

      await refreshProfile();
    } catch (err) {
      console.error('Upload failed', err);
      alert(err instanceof PhotoError ? err.message : 'We couldn\'t upload your photo. Please try again.');
    } finally {
      setUploadingSlot(null);
    }
  };

  const handleDeletePhoto = async (photoUrl: string) => {
    if (!confirm('Remove this photo?')) return;
    setDeletingUrl(photoUrl);
    const token = await getIdToken();
    try {
      await fetch(`${API_BASE}/api/auth/photos`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ photoUrl }),
      });
      await refreshProfile();
    } catch (err) {
      console.error('Delete failed', err);
    } finally {
      setDeletingUrl(null);
    }
  };

  const handleDevReset = async () => {
    if (!confirm('Reset all your likes and pairs?')) return;
    const token = await getIdToken();
    await fetch(`${API_BASE}/api/dev/reset`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    sessionStorage.removeItem('feedIndex');
    sessionStorage.removeItem('feedHistory');
    alert('Reset done — refresh the discover feed.');
  };

  const handleMasterReset = async () => {
    if (!confirm('MASTER RESET — delete ALL likes and pairs for every user? This cannot be undone.')) return;
    if (!confirm('Are you sure? This wipes the entire pairing database.')) return;
    const token = await getIdToken();
    const res = await fetch(`${API_BASE}/api/dev/master-reset`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    alert(data.message || 'Master reset done.');
    sessionStorage.removeItem('feedIndex');
    sessionStorage.removeItem('feedHistory');
    queryClient.invalidateQueries({ queryKey: ['matches'] });
    queryClient.invalidateQueries({ queryKey: ['feed'] });
  };

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: t.paper }}>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingBottom: 20 }} className="scroll-container">

        {/* Header */}
        <div style={{ padding: '10px 20px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h1 style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontWeight: 400, fontSize: 32, margin: 0, color: t.ink, letterSpacing: -0.3 }}>
            You
          </h1>
          <button onClick={onToggleTheme} aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'} style={{ all: 'unset', cursor: 'pointer', width: 38, height: 38, borderRadius: 100, background: t.card, border: `1px solid ${t.line}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {dark ? Icon.sun({ s: 18, c: t.ink }) : Icon.moon({ s: 18, c: t.ink, fill: t.ink })}
          </button>
        </div>

        {/* Couple card — only shown when in couples mode */}
        {userProfile?.mode === 'COUPLES' && (
          <CoupleCard t={t} getIdToken={getIdToken} refreshProfile={refreshProfile} />
        )}

        {/* Pair-up card — singles can invite their real-life partner anytime */}
        {userProfile?.mode === 'SINGLES' && (
          <PairUpCard t={t} getIdToken={getIdToken} refreshProfile={refreshProfile} />
        )}

        {/* Profile card */}
        <div style={{ padding: '6px 20px 14px' }}>
          <div style={{ borderRadius: 22, overflow: 'hidden', background: t.card, border: `1px solid ${t.line}`, boxShadow: t.shadow }}>
            <div style={{ display: 'flex', gap: 14, padding: 14 }}>
              {photos[0]
                ? <Img placeholder={t.paperAlt} src={photos[0]} alt="" style={{ width: 96, height: 120, borderRadius: 14, objectFit: 'cover', flexShrink: 0 }} />
                : <Portrait seed={userProfile?.id || 'me'} label="You" style={{ width: 96, height: 120, borderRadius: 14 }} />}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 26, color: t.ink, lineHeight: 1 }}>
                  {profile?.displayName || '—'}{age ? <span style={{ fontFamily: FONTS.sans, fontSize: 18, fontWeight: 300 }}>, {age}</span> : null}
                </div>
                <div style={{ fontFamily: FONTS.sans, fontSize: 12.5, color: t.inkSoft, marginTop: 4 }}>
                  {userProfile?.mode === 'COUPLES' ? 'Couples mode' : 'Singles mode'}
                </div>
                {(profile?.locationPostcode || userProfile?.university) && (
                  <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {profile?.locationPostcode && <Chip t={t} small>{profile.locationPostcode}</Chip>}
                    {/* Generic on purpose — the institution name isn't shown. */}
                    {userProfile?.university && (
                      <span data-testid="me-university-badge">
                        <Chip t={t} small>{Icon.cap({ s: 12, c: t.terracotta })} University</Chip>
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Completeness */}
            <div style={{ padding: '2px 16px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                <span style={{ fontFamily: FONTS.sans, fontSize: 12, color: t.inkSoft }}>Profile strength</span>
                <span style={{ fontFamily: FONTS.sans, fontSize: 12, color: t.ink, fontWeight: 600 }}>{completeness}%</span>
              </div>
              <div style={{ height: 6, borderRadius: 100, background: t.paperAlt, overflow: 'hidden' }}>
                <div style={{ width: `${completeness}%`, height: '100%', background: `linear-gradient(90deg, ${t.blush}, ${t.terracotta})`, transition: 'width 600ms' }} />
              </div>
              {completeness < 100 && (
                <div style={{ fontFamily: FONTS.sans, fontSize: 11.5, color: t.inkMute, marginTop: 6 }}>
                  {!profile?.bio ? 'Add a bio to improve your pairings.' : !profile?.photos?.length ? 'Add photos to stand out.' : 'Almost there.'}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', borderTop: `1px solid ${t.line}` }}>
              <ProfileAction t={t} icon={Icon.edit({ s: 16, c: t.ink })} label="Edit" onClick={() => setShowEdit(true)} />
              <ProfileAction t={t} icon={Icon.compass({ s: 16, c: t.ink })} label="Preview" onClick={() => setShowPreview(true)} />
              <ProfileAction t={t} icon={Icon.sparkle({ s: 16, c: t.terracotta })} label="Pre+" onClick={() => alert('Pre+ subscription is not yet available.')} last />
            </div>
          </div>
        </div>

        {/* Bio */}
        <div style={{ padding: '6px 24px 6px' }}>
          <Label t={t}>Bio</Label>
          <div style={{ marginTop: 8, padding: '16px 18px', borderRadius: 16, background: t.card, border: `1px dashed ${t.line}`, position: 'relative', minHeight: 60 }}>
            {editingBio ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <textarea 
                  value={bioInput} onChange={(e) => setBioInput(e.target.value)} 
                  placeholder="Write something interesting..."
                  style={{ all: 'unset', fontFamily: FONTS.sans, fontSize: 14, color: t.ink, width: '100%', minHeight: 60, boxSizing: 'border-box' }} 
                  autoFocus 
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button onClick={() => setEditingBio(false)} style={{ all: 'unset', cursor: 'pointer', fontFamily: FONTS.sans, fontSize: 12, fontWeight: 600, color: t.inkSoft, padding: '4px 10px' }}>Cancel</button>
                  <button onClick={async () => { await saveField('bio', bioInput); setEditingBio(false); }} style={{ all: 'unset', cursor: 'pointer', fontFamily: FONTS.sans, fontSize: 12, fontWeight: 600, color: t.paper, background: t.ink, padding: '4px 12px', borderRadius: 100 }}>
                    {savingField === 'bio' ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            ) : (
              <>
                {profile?.bio ? (
                  <p style={{ fontFamily: FONTS.display, fontSize: 17, lineHeight: 1.4, color: t.ink, margin: 0, fontStyle: 'italic' }}>
                    "{profile.bio}"
                  </p>
                ) : (
                  <p style={{ fontFamily: FONTS.sans, fontSize: 14, color: t.inkMute, margin: 0, fontStyle: 'italic' }}>
                    No bio yet — add one to stand out.
                  </p>
                )}
                <button onClick={() => { setEditingBio(true); setBioInput(profile?.bio || ''); }} style={{ all: 'unset', cursor: 'pointer', position: 'absolute', top: 10, right: 10, width: 28, height: 28, borderRadius: 100, background: t.paperAlt, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {Icon.edit({ s: 13, c: t.inkSoft })}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Photos */}
        <div style={{ padding: '16px 20px 6px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '0 4px 10px' }}>
            <Label t={t}>Photos</Label>
            <span style={{ fontFamily: FONTS.mono, fontSize: 11, color: t.inkMute }}>{photos.length} / 6</span>
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {Array.from({ length: 6 }).map((_, i) => {
              const url = photos[i];
              const isUploading = uploadingSlot === i;
              const isDeleting = url && deletingUrl === url;
              return url ? (
                <div key={i} style={{ aspectRatio: '3/4', borderRadius: 12, overflow: 'hidden', position: 'relative', background: t.card }}>
                  <Img placeholder={t.paperAlt} src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: isDeleting ? 0.4 : 1 }} />
                  {i === 0 && <div style={{ position: 'absolute', top: 6, left: 6, padding: '3px 6px', borderRadius: 4, background: t.ink, color: t.paper, fontFamily: FONTS.mono, fontSize: 9, fontWeight: 600 }}>MAIN</div>}
                  <button onClick={() => handleDeletePhoto(url)} style={{ all: 'unset', cursor: 'pointer', position: 'absolute', top: 6, right: 6, width: 24, height: 24, borderRadius: 100, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {Icon.x({ s: 11, c: '#fff', w: 2.5 })}
                  </button>
                </div>
              ) : (
                <button key={i} onClick={() => handlePhotoSlotTap(i)} style={{ all: 'unset', cursor: 'pointer', aspectRatio: '3/4', borderRadius: 12, background: t.card, border: `1.5px dashed ${t.line}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {isUploading
                    ? <div style={{ width: 20, height: 20, borderRadius: '50%', border: `2px solid ${t.terracotta}`, borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
                    : Icon.plus({ s: 20, c: t.inkMute })}
                </button>
              );
            })}
          </div>
        </div>

        {/* Spark Answers / Prompts */}
        {sparkAnswers.length > 0 && (
          <div style={{ padding: '18px 20px 6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '0 4px 10px' }}>
              <Label t={t}>Spark answers</Label>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {sparkAnswers.map((s, i) => (
                <div key={i} style={{ padding: '14px 16px', borderRadius: 14, background: t.card, border: `1px solid ${t.line}` }}>
                  <div style={{ fontFamily: FONTS.sans, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.6, color: t.inkMute, marginBottom: 4 }}>
                    {s.prompt}
                  </div>
                  <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 16, color: t.ink, lineHeight: 1.35 }}>
                    "{s.answer}"
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Preferences */}
        <PreferencesSection t={t} profile={profile} userProfile={userProfile} getIdToken={getIdToken} refreshProfile={refreshProfile} onEditDiscovery={() => setShowEdit(true)} />

        {/* Feedback */}
        <div style={{ padding: '10px 20px 4px' }}>
          <FeedbackButton t={t} userEmail={userProfile?.email} />
        </div>

        {/* Privacy choices — withdrawing consent must be as easy as giving it */}
        <div style={{ padding: '4px 20px 4px' }}>
          <button data-testid="me-privacy-choices" onClick={openConsentChoices} style={{
            all: 'unset', cursor: 'pointer', width: '100%', padding: '14px', borderRadius: 14,
            background: t.card, border: `1px solid ${t.line}`, boxSizing: 'border-box',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            fontFamily: FONTS.sans, fontSize: 14, color: t.ink, fontWeight: 500,
          }}>
            <span>Privacy choices</span>
            <ConsentStatus t={t} />
          </button>
        </div>

        {/* Admin — only visible to ADMIN role (server independently enforces access) */}
        {isAdmin && (
          <div style={{ padding: '4px 20px 8px' }}>
            <button onClick={() => setShowAdmin(true)} className="pm-press" style={{
              all: 'unset', cursor: 'pointer', width: '100%', padding: '14px', borderRadius: 14,
              background: t.ink, textAlign: 'center', boxSizing: 'border-box',
              fontFamily: FONTS.sans, fontSize: 14, color: t.paper, fontWeight: 600,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}>
              {Icon.sparkle({ s: 14, c: t.paper })} Control panel
            </button>
          </div>
        )}

        {/* Sign out */}
        <div style={{ padding: '4px 20px 8px' }}>
          <button onClick={signOut} style={{
            all: 'unset', cursor: 'pointer', width: '100%', padding: '14px', borderRadius: 14,
            background: t.card, border: `1px solid ${t.line}`, textAlign: 'center',
            fontFamily: FONTS.sans, fontSize: 14, color: t.terracotta, fontWeight: 500,
            boxSizing: 'border-box',
          }}>
            Sign out
          </button>
        </div>

        {/* Account — suspend / delete */}
        <AccountSection t={t} userProfile={userProfile} getIdToken={getIdToken} refreshProfile={refreshProfile} signOut={signOut} />

        {/* Dev tools */}
        {IS_DEV && (
          <div style={{ padding: '4px 20px 8px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button onClick={handleDevReset} style={{
              all: 'unset', cursor: 'pointer', width: '100%', padding: '12px', borderRadius: 14,
              background: 'transparent', border: `1px dashed ${t.line}`, textAlign: 'center',
              fontFamily: FONTS.mono, fontSize: 11, color: t.inkMute,
              boxSizing: 'border-box',
            }}>
              [DEV] Reset likes & matches
            </button>
            <button onClick={handleMasterReset} style={{
              all: 'unset', cursor: 'pointer', width: '100%', padding: '12px', borderRadius: 14,
              background: 'transparent', border: `1px dashed ${t.terracotta}`, textAlign: 'center',
              fontFamily: FONTS.mono, fontSize: 11, color: t.terracotta,
              boxSizing: 'border-box',
            }}>
              [DEV] Master reset — all users
            </button>
            <button onClick={async () => {
              const res = await fetch(`${API_BASE}/api/test/match-email`, { method: 'POST' });
              const data = await res.json();
              alert(res.ok ? `Email sent! ID: ${data.id}` : `Failed: ${data.error}`);
            }} style={{
              all: 'unset', cursor: 'pointer', width: '100%', padding: '12px', borderRadius: 14,
              background: 'transparent', border: `1px dashed ${t.sage}`, textAlign: 'center',
              fontFamily: FONTS.mono, fontSize: 11, color: t.sage,
              boxSizing: 'border-box',
            }}>
              [DEV] Send test match email
            </button>
          </div>
        )}

        {/* Footer */}
        <div style={{ textAlign: 'center', padding: '20px 20px 10px' }}>
          <Logo t={t} size={0.75} />
          <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: t.inkMute, marginTop: 6, letterSpacing: 1.2, textTransform: 'uppercase' }}>
            Made in London · v1.0
          </div>
        </div>
      </div>
      {showEdit && (
        <EditProfileSheet
          t={t}
          profile={profile}
          onClose={() => setShowEdit(false)}
          onSave={async (fields) => {
            setSavingField('edit');
            const token = await getIdToken();
            try {
              await fetch(`${API_BASE}/api/auth/profile`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(fields),
              });
              await refreshProfile();
            } catch (e) { console.error(e); }
            finally { setSavingField(null); }
            setShowEdit(false);
          }}
          saving={savingField === 'edit'}
        />
      )}

      {showPreview && (
        <ProfilePreview userProfile={userProfile} t={t} onBack={() => setShowPreview(false)} />
      )}

      {showAdmin && isAdmin && (
        <AdminPanel t={t} onClose={() => setShowAdmin(false)} />
      )}
    </div>
  );
}

function ProfileAction({ t, icon, label, last, onClick }: { t: Theme; icon: React.ReactNode; label: string; last?: boolean; onClick?: () => void }) {
  return (
    <button onClick={onClick} style={{ all: 'unset', cursor: 'pointer', flex: 1, padding: '12px 8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, borderRight: last ? 'none' : `1px solid ${t.line}`, fontFamily: FONTS.sans, fontSize: 12.5, fontWeight: 500, color: t.ink }}>
      {icon}{label}
    </button>
  );
}

// ─── Account — suspend / delete ───────────────────────────────────
// A user's right: each action is explained in full before the person can
// confirm it. Suspend is reversible (hides the profile, existing matches
// still work); delete is permanent (GDPR erasure via DELETE /auth/account).

type AccountSheetKind = 'suspend' | 'reactivate' | 'delete' | null;

function AccountSection({ t, userProfile, getIdToken, refreshProfile, signOut }: {
  t: Theme;
  userProfile: any;
  getIdToken: () => Promise<string | null>;
  refreshProfile: () => Promise<void>;
  signOut: () => void;
}) {
  const [sheet, setSheet] = useState<AccountSheetKind>(null);
  const [working, setWorking] = useState(false);
  const [sheetError, setSheetError] = useState('');
  const isActive = userProfile?.isActive !== false;

  const handleSuspend = async () => {
    setWorking(true);
    try {
      const token = await getIdToken();
      await fetch(`${API_BASE}/api/auth/suspend`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      await refreshProfile();
      setSheet(null);
    } finally {
      setWorking(false);
    }
  };

  const handleReactivate = async () => {
    setWorking(true);
    try {
      const token = await getIdToken();
      await fetch(`${API_BASE}/api/auth/reactivate`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      await refreshProfile();
      setSheet(null);
    } finally {
      setWorking(false);
    }
  };

  const handleDelete = async () => {
    setWorking(true);
    setSheetError('');
    try {
      const token = await getIdToken();
      const res = await fetch(`${API_BASE}/api/auth/account`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        // Only sign out when the account is actually gone — otherwise the user
        // would believe it's deleted while it still exists.
        setSheetError("Something went wrong — your account was NOT deleted. Please try again or contact us.");
        setWorking(false);
        return;
      }
      signOut();
    } catch {
      setSheetError("Couldn't reach the server — your account was NOT deleted. Check your connection and try again.");
      setWorking(false);
    }
  };

  return (
    <div style={{ padding: '4px 20px 8px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <button onClick={() => setSheet(isActive ? 'suspend' : 'reactivate')} style={{
        all: 'unset', cursor: 'pointer', width: '100%', padding: '14px', borderRadius: 14,
        background: t.card, border: `1px solid ${t.line}`, textAlign: 'center',
        fontFamily: FONTS.sans, fontSize: 14, color: t.ink, fontWeight: 500,
        boxSizing: 'border-box',
      }}>
        {isActive ? 'Suspend my account' : 'Reactivate my account'}
      </button>

      <button onClick={() => setSheet('delete')} style={{
        all: 'unset', cursor: 'pointer', width: '100%', padding: '14px', borderRadius: 14,
        background: 'transparent', border: `1px solid ${t.terracotta}55`, textAlign: 'center',
        fontFamily: FONTS.sans, fontSize: 14, color: t.terracotta, fontWeight: 500,
        boxSizing: 'border-box',
      }}>
        Delete my account
      </button>

      {sheet === 'suspend' && (
        <AccountActionSheet
          t={t}
          title="Suspend your account?"
          body="Your profile is hidden from everyone's feed immediately — nobody new will be shown you, and you won't be shown anyone new. You can still message people you've already been paired with. Reactivate any time from this same screen, with everything exactly as you left it."
          confirmLabel="Suspend account"
          onConfirm={handleSuspend}
          onClose={() => setSheet(null)}
          confirming={working}
        />
      )}

      {sheet === 'reactivate' && (
        <AccountActionSheet
          t={t}
          title="Reactivate your account?"
          body="You'll be visible in the feed again and start getting new pairs right away. Everything from before your suspension — pairs, chats, photos — is still exactly as you left it."
          confirmLabel="Reactivate account"
          onConfirm={handleReactivate}
          onClose={() => setSheet(null)}
          confirming={working}
        />
      )}

      {sheet === 'delete' && (
        <AccountActionSheet
          t={t}
          title="Delete your account?"
          danger
          body="This permanently deletes your profile, photos, pairs, messages and booking history. There is no way to undo this or recover your data afterwards. If you just want a break, suspending is reversible — this is not."
          confirmLabel="Delete permanently"
          error={sheetError}
          onConfirm={handleDelete}
          onClose={() => { setSheet(null); setSheetError(''); }}
          confirming={working}
        />
      )}
    </div>
  );
}

function AccountActionSheet({ t, title, body, confirmLabel, danger, error, onConfirm, onClose, confirming }: {
  t: Theme; title: string; body: string; confirmLabel: string; danger?: boolean; error?: string;
  onConfirm: () => void; onClose: () => void; confirming: boolean;
}) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 300,
        background: 'rgba(10,8,6,0.55)',
        backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'flex-end',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 430, margin: '0 auto',
          background: t.paper, borderRadius: '24px 24px 0 0',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <div style={{ padding: '24px 22px 8px' }}>
          <div style={{ width: 40, height: 4, borderRadius: 2, background: t.line, margin: '0 auto 20px' }} />
          <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 26, color: danger ? t.terracotta : t.ink, marginBottom: 10, lineHeight: 1.15 }}>
            {title}
          </div>
          <p style={{ fontFamily: FONTS.sans, fontSize: 14, color: t.inkSoft, lineHeight: 1.6, margin: '0 0 24px' }}>
            {body}
          </p>
          {error && (
            <div style={{
              margin: '0 0 16px', padding: '12px 14px', borderRadius: 12,
              background: `${t.terracotta}12`, border: `1px solid ${t.terracotta}44`,
              fontFamily: FONTS.sans, fontSize: 13, color: t.terracotta, lineHeight: 1.5,
            }}>
              {error}
            </div>
          )}
        </div>
        <div style={{ padding: '0 22px 28px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            onClick={onConfirm}
            disabled={confirming}
            style={{
              all: 'unset', cursor: confirming ? 'default' : 'pointer', width: '100%', boxSizing: 'border-box',
              padding: '15px', borderRadius: 14, textAlign: 'center',
              fontFamily: FONTS.sans, fontSize: 15, fontWeight: 600,
              background: danger ? t.terracotta : t.ink, color: '#fff',
              opacity: confirming ? 0.6 : 1,
            }}
          >
            {confirming ? 'Working…' : confirmLabel}
          </button>
          <button
            onClick={onClose}
            disabled={confirming}
            style={{
              all: 'unset', cursor: confirming ? 'default' : 'pointer', width: '100%', textAlign: 'center',
              padding: '10px', fontFamily: FONTS.sans, fontSize: 13, color: t.inkMute,
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

const GENDER_OPTIONS = [
  { key: 'man', label: 'Man' },
  { key: 'woman', label: 'Woman' },
  { key: 'nonbinary', label: 'Non-binary' },
  { key: 'other', label: 'Other' },
];

const SEXUALITY_OPTIONS = [
  { key: 'straight', label: 'Straight' },
  { key: 'gay', label: 'Gay' },
  { key: 'bisexual', label: 'Bisexual' },
  { key: 'pansexual', label: 'Pansexual' },
  { key: 'asexual', label: 'Asexual' },
  { key: 'prefer_not_to_say', label: 'Prefer not to say' },
];

const RELATIONSHIP_GOAL_OPTIONS = [
  { key: 'relationship', label: 'Relationship' },
  { key: 'something_casual', label: 'Something casual' },
  { key: 'open_to_both', label: 'Open to both' },
  { key: 'friends_first', label: 'Friends first' },
];

function cap(s: string) { return s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' '); }

interface PrefPickerProps {
  t: Theme;
  title: string;
  options: { key: string; label: string }[];
  selected: string[];
  multi?: boolean;
  onSave: (keys: string[]) => void;
  onClose: () => void;
}

function PrefPicker({ t, title, options, selected, multi, onSave, onClose }: PrefPickerProps) {
  const [current, setCurrent] = useState<string[]>(selected);

  const toggle = (key: string) => {
    if (multi) {
      setCurrent(c => c.includes(key) ? c.filter(k => k !== key) : [...c, key]);
    } else {
      setCurrent([key]);
    }
  };

  return (
    <div data-clarity-mask="true" style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} />
      <div style={{
        position: 'relative', background: t.paper, borderRadius: '20px 20px 0 0',
        maxWidth: 430, width: '100%', margin: '0 auto',
        maxHeight: '85vh', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '16px 20px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${t.line}`, flexShrink: 0 }}>
          <span style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 20, color: t.ink }}>{title}</span>
          <button onClick={onClose} style={{ all: 'unset', cursor: 'pointer', width: 30, height: 30, borderRadius: 100, background: t.card, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {Icon.x({ s: 13, c: t.inkSoft, w: 2.5 })}
          </button>
        </div>
        {/* Options — scrollable if they overflow */}
        <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 6, overflowY: 'auto', flex: 1 }}>
          {options.map(o => {
            const sel = current.includes(o.key);
            return (
              <button key={o.key} onClick={() => toggle(o.key)} style={{
                all: 'unset', cursor: 'pointer', padding: '13px 16px', borderRadius: 14,
                background: sel ? t.ink : t.card, border: `1px solid ${sel ? t.ink : t.line}`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                fontFamily: FONTS.sans, fontSize: 15, fontWeight: 500,
                color: sel ? t.paper : t.ink, transition: 'all 120ms',
              }}>
                {o.label}
                {sel && Icon.check({ s: 15, c: t.paper, w: 2.5 })}
              </button>
            );
          })}
        </div>
        {/* Save — always visible, respects iOS home indicator */}
        <div style={{
          padding: '8px 16px',
          paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
          flexShrink: 0, borderTop: `1px solid ${t.line}`,
          background: t.paper,
        }}>
          <button onClick={() => { if (current.length > 0) { onSave(current); onClose(); } }} style={{
            all: 'unset', cursor: 'pointer', width: '100%', padding: '14px', borderRadius: 14,
            background: t.ink, color: t.paper, textAlign: 'center', boxSizing: 'border-box',
            fontFamily: FONTS.sans, fontSize: 15, fontWeight: 600,
          }}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function PreferencesSection({ t, profile, userProfile, getIdToken, refreshProfile, onEditDiscovery }: {
  t: Theme;
  profile: any;
  userProfile: any;
  getIdToken: () => Promise<string | null>;
  refreshProfile: () => Promise<void>;
  // Opens the Edit profile sheet, which already edits postcode / radius /
  // budget together. The Discovery rows below route here rather than getting
  // their own pickers, so there's one save path for those fields instead of two.
  onEditDiscovery: () => void;
}) {
  const [saving, setSaving] = useState<string | null>(null);
  const [picker, setPicker] = useState<null | 'gender' | 'seeking' | 'sexuality' | 'goal' | 'ethnicity' | 'nationality'>(null);

  const save = async (field: string, value: string | string[] | null) => {
    setSaving(field);
    const token = await getIdToken();
    await fetch(`${API_BASE}/api/auth/profile`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ [field]: value }),
    });
    await refreshProfile();
    setSaving(null);
  };

  const genderLabel = profile?.gender ? cap(profile.gender) : 'Not set';
  const seekingLabel = profile?.seekingGenders?.length ? profile.seekingGenders.map(cap).join(', ') : 'Not set';
  const sexualityLabel = profile?.sexuality ? cap(profile.sexuality) : 'Not set';
  const goalLabel = profile?.relationshipGoal ? cap(profile.relationshipGoal) : 'Not set';
  const ethnicityValue = profile?.ethnicity
    ? (ETHNICITY_OPTIONS.find(o => o.key === profile.ethnicity)?.label ?? cap(profile.ethnicity))
    : 'Not set';
  const nationalityValue = profile?.nationality || 'Not set';

  return (
    <>
      <div data-clarity-mask="true" style={{ padding: '20px 20px 8px' }}>
        <Label t={t}>About you</Label>
        <div style={{ marginTop: 10, borderRadius: 16, background: t.card, border: `1px solid ${t.line}`, overflow: 'hidden' }}>
          <EditableRow t={t} label="Gender" value={genderLabel} loading={saving === 'gender'} onClick={() => setPicker('gender')} />
          <EditableRow t={t} label="Sexuality" value={sexualityLabel} loading={saving === 'sexuality'} onClick={() => setPicker('sexuality')} />
          <EditableRow t={t} label="I'm looking for" value={seekingLabel} loading={saving === 'seeking'} onClick={() => setPicker('seeking')} />
          <EditableRow t={t} label="Relationship goal" value={goalLabel} loading={saving === 'goal'} onClick={() => setPicker('goal')} />
          <EditableRow t={t} label="Ethnicity" value={ethnicityValue} loading={saving === 'ethnicity'} onClick={() => setPicker('ethnicity')} />
          <EditableRow t={t} label="Nationality" value={nationalityValue} loading={saving === 'nationality'} onClick={() => setPicker('nationality')} last />
        </div>
        <div style={{ marginTop: 8, fontFamily: FONTS.sans, fontSize: 11, color: t.inkMute, lineHeight: 1.5, padding: '0 4px' }}>
          Ethnicity and nationality are optional — shown on your profile if set, never used for matching.
        </div>
      </div>

      <div style={{ padding: '4px 20px 8px' }}>
        <Label t={t}>Discovery</Label>
        <div style={{ marginTop: 10, borderRadius: 16, background: t.card, border: `1px solid ${t.line}`, overflow: 'hidden' }}>
          {/* Mode is deliberately not tappable: switching to Couples isn't a
              setting, it requires actually pairing with a partner. The "Here
              with someone?" banner above this section is that flow. */}
          <EditableRow t={t} label="Mode" value={userProfile?.mode === 'COUPLES' ? 'Couples' : 'Singles'} readOnly />
          <EditableRow t={t} label="Postcode" value={profile?.locationPostcode || 'Not set'} onClick={onEditDiscovery} />
          <EditableRow t={t} label="Radius" value={profile?.locationRadiusMiles ? `${profile.locationRadiusMiles} miles` : 'Not set'} onClick={onEditDiscovery} />
          <EditableRow t={t} label="Budget" value={profile?.budgetMidpoint ? `£${profile.budgetMidpoint}/person` : 'Not set'} onClick={onEditDiscovery} />
          {/* Status, not a setting — there is no per-user toggle for this
              behind it (no such field exists on Profile), so it must not
              render as something you can tap and change. */}
          <EditableRow t={t} label="AI date planner" value="On" pillColor={t.sage} readOnly last />
        </div>
      </div>

      {picker === 'gender' && (
        <PrefPicker t={t} title="Gender" options={GENDER_OPTIONS} selected={profile?.gender ? [profile.gender] : []}
          onSave={([v]) => save('gender', v)} onClose={() => setPicker(null)} />
      )}
      {picker === 'sexuality' && (
        <PrefPicker t={t} title="Sexuality" options={SEXUALITY_OPTIONS} selected={profile?.sexuality ? [profile.sexuality] : []}
          onSave={([v]) => save('sexuality', v)} onClose={() => setPicker(null)} />
      )}
      {picker === 'seeking' && (
        <PrefPicker t={t} title="I'm looking for" options={GENDER_OPTIONS} selected={profile?.seekingGenders || []}
          multi onSave={(v) => save('seekingGenders', v)} onClose={() => setPicker(null)} />
      )}
      {picker === 'goal' && (
        <PrefPicker t={t} title="Relationship goal" options={RELATIONSHIP_GOAL_OPTIONS} selected={profile?.relationshipGoal ? [profile.relationshipGoal] : []}
          onSave={([v]) => save('relationshipGoal', v)} onClose={() => setPicker(null)} />
      )}
      {picker === 'ethnicity' && (
        <PrefPicker t={t} title="Ethnicity" options={[...ETHNICITY_OPTIONS]} selected={profile?.ethnicity ? [profile.ethnicity] : []}
          onSave={([v]) => save('ethnicity', v)} onClose={() => setPicker(null)} />
      )}
      {picker === 'nationality' && (
        <PrefPicker t={t} title="Nationality"
          options={[{ key: '__clear__', label: 'Prefer not to say' }, ...COUNTRIES.map(c => ({ key: c, label: c }))]}
          selected={profile?.nationality ? [profile.nationality] : []}
          onSave={([v]) => save('nationality', v === '__clear__' ? null : v)} onClose={() => setPicker(null)} />
      )}
    </>
  );
}

// ─── Edit Profile Sheet ──────────────────────────────────────────

const RADIUS_OPTIONS = [1, 3, 5, 10, 15, 20, 25, 30];
const BUDGET_OPTIONS = [15, 20, 30, 40, 50, 60, 80, 100];

function EditProfileSheet({ t, profile, onClose, onSave, saving }: {
  t: Theme;
  profile: any;
  onClose: () => void;
  onSave: (fields: Record<string, any>) => Promise<void>;
  saving: boolean;
}) {
  const [displayName, setDisplayName] = useState(profile?.displayName || '');
  const [bio, setBio] = useState(profile?.bio || '');
  const [postcode, setPostcode] = useState(profile?.locationPostcode || '');
  const [radius, setRadius] = useState<number>(profile?.locationRadiusMiles || 10);
  const [budget, setBudget] = useState<number>(profile?.budgetMidpoint || 40);

  const inp = (val: string, set: (v: string) => void, placeholder: string, multiline?: boolean) => {
    const base: React.CSSProperties = {
      all: 'unset' as any, width: '100%', boxSizing: 'border-box' as any,
      fontFamily: FONTS.sans, fontSize: 14, color: t.ink, lineHeight: 1.5,
      padding: '12px 14px', borderRadius: 12, background: t.paperAlt,
      border: `1px solid ${t.line}`,
    };
    if (multiline) return (
      <textarea value={val} onChange={e => set(e.target.value)} placeholder={placeholder}
        rows={3}
        style={{ ...base, resize: 'none' as any, display: 'block' }} />
    );
    return <input value={val} onChange={e => set(e.target.value)} placeholder={placeholder} style={base} />;
  };

  const chipRow = (options: number[], current: number, set: (v: number) => void, fmt: (v: number) => string) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {options.map(o => (
        <button key={o} onClick={() => set(o)} style={{
          all: 'unset', cursor: 'pointer', padding: '7px 14px', borderRadius: 100,
          fontFamily: FONTS.sans, fontSize: 13, fontWeight: 500,
          background: current === o ? t.ink : t.card,
          color: current === o ? t.paper : t.ink,
          border: `1px solid ${current === o ? t.ink : t.line}`,
        }}>{fmt(o)}</button>
      ))}
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-end' }}
      onClick={onClose}>
      <div style={{ width: '100%', maxWidth: 430, margin: '0 auto', background: t.paper, borderRadius: '24px 24px 0 0', maxHeight: '90dvh', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '20px 20px 14px', borderBottom: `1px solid ${t.line}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <span style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 22, color: t.ink }}>Edit profile</span>
          <button onClick={onClose} style={{ all: 'unset', cursor: 'pointer', width: 32, height: 32, borderRadius: '50%', background: t.paperAlt, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {Icon.x({ s: 16, c: t.inkMute })}
          </button>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 20px 8px', display: 'flex', flexDirection: 'column', gap: 20 }}
          className="scroll-container">

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontFamily: FONTS.sans, fontSize: 12, fontWeight: 600, color: t.inkMute, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Display name</span>
            {inp(displayName, setDisplayName, 'Your name')}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontFamily: FONTS.sans, fontSize: 12, fontWeight: 600, color: t.inkMute, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Bio</span>
            {inp(bio, setBio, 'Tell people about yourself...', true)}
            <span style={{ fontFamily: FONTS.sans, fontSize: 11, color: t.inkMute }}>{bio.length} / 500</span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontFamily: FONTS.sans, fontSize: 12, fontWeight: 600, color: t.inkMute, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Postcode</span>
            {inp(postcode, v => setPostcode(v.toUpperCase()), 'e.g. E1 6RF')}
            <span style={{ fontFamily: FONTS.sans, fontSize: 11, color: t.inkMute }}>Used for pairing — only the area is shown, never your exact address.</span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontFamily: FONTS.sans, fontSize: 12, fontWeight: 600, color: t.inkMute, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Search radius</span>
            {chipRow(RADIUS_OPTIONS, radius, setRadius, v => `${v} mi`)}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontFamily: FONTS.sans, fontSize: 12, fontWeight: 600, color: t.inkMute, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Budget per person</span>
            {chipRow(BUDGET_OPTIONS, budget, setBudget, v => `£${v}`)}
          </div>
        </div>

        {/* Save button */}
        <div style={{ padding: '14px 20px 32px', flexShrink: 0, borderTop: `1px solid ${t.line}` }}>
          <button
            onClick={() => onSave({
              ...(displayName.trim() && { displayName: displayName.trim() }),
              bio: bio.trim(),
              ...(postcode.trim() && { locationPostcode: postcode.trim() }),
              locationRadiusMiles: radius,
              budgetMidpoint: budget,
            })}
            disabled={saving || !displayName.trim()}
            style={{
              all: 'unset', cursor: saving ? 'default' : 'pointer', width: '100%',
              padding: '15px', borderRadius: 14, textAlign: 'center', boxSizing: 'border-box',
              fontFamily: FONTS.sans, fontSize: 15, fontWeight: 600,
              background: saving ? t.paperAlt : t.ink, color: saving ? t.inkMute : t.paper,
              transition: 'all 150ms',
            }}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Couple Card ─────────────────────────────────────────────────

const API_BASE_ME = import.meta.env.VITE_API_URL || 'http://localhost:8080';

// Persistent "waiting for your partner" card — shown whenever mode is COUPLES
// but no couple exists yet. This is what stops the invite code from ever
// being un-retrievable: however the creation sheet gets dismissed (Done,
// backdrop tap, app restart), the code lives here until the partner joins
// or the invite is cancelled.
function WaitingInviteCard({ t, getIdToken, refreshProfile }: {
  t: Theme; getIdToken: () => Promise<string | null>; refreshProfile: () => Promise<void>;
}) {
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState<'code' | 'link' | null>(null);
  const [resendLocked, setResendLocked] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  // NOTE: shape must match Discover's ['couple-invite'] query exactly — same
  // key means a shared cache entry, so both must store the bare invite object.
  const { data: invite, isLoading } = useQuery({
    queryKey: ['couple-invite'],
    queryFn: async () => {
      const token = await getIdToken();
      const res = await fetch(`${API_BASE_ME}/api/couples/invite`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return null;
      return ((await res.json()).invite ?? null) as { code: string; link: string; email: string | null } | null;
    },
    staleTime: 15_000,
  });

  const resendMutation = useMutation({
    mutationFn: async () => {
      const token = await getIdToken();
      const res = await fetch(`${API_BASE_ME}/api/couples/invite/resend`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to resend');
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async () => {
      const token = await getIdToken();
      await fetch(`${API_BASE_ME}/api/couples/invite`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['couple-invite'] });
      queryClient.invalidateQueries({ queryKey: ['couple-me'] });
      queryClient.invalidateQueries({ queryKey: ['feed'] });
      await refreshProfile(); // mode flips back to SINGLES server-side on cancel
    },
  });

  const copy = async (value: string, which: 'code' | 'link') => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(which);
      setTimeout(() => setCopied(null), 1800);
    } catch { /* clipboard unavailable */ }
  };

  const handleResend = () => {
    if (resendLocked) return;
    resendMutation.mutate();
    setResendLocked(true);
    setTimeout(() => setResendLocked(false), 30_000);
  };

  if (isLoading) return null;
  if (!invite) return null; // invite was cancelled/expired elsewhere — nothing to show

  return (
    <div style={{ padding: '0 20px 14px' }}>
      <div style={{
        borderRadius: 20, overflow: 'hidden',
        border: `1.5px solid ${t.gold}55`,
        background: `linear-gradient(135deg, ${t.gold}12, ${t.blush}08)`,
        boxShadow: `0 2px 12px ${t.gold}18`,
      }}>
        <div style={{ padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${t.gold}`, borderTopColor: 'transparent', animation: 'spin 1.2s linear infinite', flexShrink: 0 }} />
            <div>
              <div style={{ fontFamily: FONTS.mono, fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.12em', color: t.gold }}>Waiting for your partner</div>
              {invite.email && (
                <div style={{ fontFamily: FONTS.sans, fontSize: 12, color: t.inkSoft, marginTop: 2 }}>Sent to {invite.email}</div>
              )}
            </div>
          </div>

          <div
            onClick={() => copy(invite.code, 'code')}
            style={{
              cursor: 'pointer', padding: '12px 16px', borderRadius: 12, textAlign: 'center',
              background: t.card, border: `1px solid ${t.line}`, marginBottom: 10,
              fontFamily: FONTS.mono, fontSize: 18, fontWeight: 700, letterSpacing: 3, color: t.ink,
            }}
          >
            {invite.code}
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={() => copy(invite.code, 'code')} className="pm-press-soft" style={{
              all: 'unset', cursor: 'pointer', padding: '8px 14px', borderRadius: 100,
              background: t.card, border: `1px solid ${t.line}`,
              fontFamily: FONTS.sans, fontSize: 12, fontWeight: 500, color: t.ink,
            }}>
              {copied === 'code' ? 'Copied ✓' : 'Copy code'}
            </button>
            <button onClick={() => copy(invite.link, 'link')} className="pm-press-soft" style={{
              all: 'unset', cursor: 'pointer', padding: '8px 14px', borderRadius: 100,
              background: t.card, border: `1px solid ${t.line}`,
              fontFamily: FONTS.sans, fontSize: 12, fontWeight: 500, color: t.ink,
            }}>
              {copied === 'link' ? 'Copied ✓' : 'Copy link'}
            </button>
            {invite.email && (
              <button onClick={handleResend} disabled={resendLocked || resendMutation.isPending} className="pm-press-soft" style={{
                all: 'unset', cursor: 'pointer', padding: '8px 14px', borderRadius: 100,
                background: t.card, border: `1px solid ${t.line}`,
                fontFamily: FONTS.sans, fontSize: 12, fontWeight: 500, color: t.ink,
                opacity: resendLocked || resendMutation.isPending ? 0.5 : 1,
              }}>
                {resendLocked ? 'Sent ✓' : resendMutation.isPending ? 'Sending…' : 'Resend email'}
              </button>
            )}
          </div>
        </div>

        {/* Cancel */}
        <div style={{ borderTop: `1px solid ${t.gold}28` }}>
          {!confirmCancel ? (
            <button onClick={() => setConfirmCancel(true)} style={{
              all: 'unset', cursor: 'pointer', width: '100%', boxSizing: 'border-box',
              padding: '12px 8px', textAlign: 'center',
              fontFamily: FONTS.sans, fontSize: 12.5, fontWeight: 500, color: t.inkSoft,
            }}>
              Cancel invite and continue solo
            </button>
          ) : (
            <div style={{ padding: '10px 16px 14px', display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
              <div style={{ fontFamily: FONTS.sans, fontSize: 12, color: t.inkMute, textAlign: 'center' }}>
                This switches you back to solo mode — sure?
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setConfirmCancel(false)} style={{
                  all: 'unset', cursor: 'pointer', padding: '8px 16px', borderRadius: 100,
                  background: t.card, border: `1px solid ${t.line}`,
                  fontFamily: FONTS.sans, fontSize: 12, color: t.ink,
                }}>
                  Never mind
                </button>
                <button onClick={() => cancelMutation.mutate()} disabled={cancelMutation.isPending} style={{
                  all: 'unset', cursor: 'pointer', padding: '8px 16px', borderRadius: 100,
                  background: t.terracotta, color: '#fff',
                  fontFamily: FONTS.sans, fontSize: 12, fontWeight: 600,
                }}>
                  {cancelMutation.isPending ? 'Switching…' : 'Yes, go solo'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CoupleCard({ t, getIdToken, refreshProfile }: {
  t: Theme; getIdToken: () => Promise<string | null>; refreshProfile: () => Promise<void>;
}) {
  const [dissolving, setDissolving] = useState(false);
  const queryClient = useQueryClient();

  // NOTE: shape must match Discover's ['couple-me'] query exactly — same key
  // means a shared cache entry, so both must store the bare couple object.
  const { data: couple, isLoading } = useQuery({
    queryKey: ['couple-me'],
    queryFn: async () => {
      const token = await getIdToken();
      const res = await fetch(`${API_BASE_ME}/api/couples/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return null;
      return ((await res.json()).couple ?? null) as any | null;
    },
    staleTime: 30_000,
  });

  const handleActivate = async () => {
    const token = await getIdToken();
    await fetch(`${API_BASE_ME}/api/couples/activate`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    queryClient.invalidateQueries({ queryKey: ['couple-me'] });
  };

  const handleDissolve = async () => {
    if (!confirm('Take a break from double dating? Your partner will be notified.')) return;
    setDissolving(true);
    const token = await getIdToken();
    await fetch(`${API_BASE_ME}/api/couples/dissolve`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    queryClient.invalidateQueries({ queryKey: ['couple-me'] });
    setDissolving(false);
  };

  // In COUPLES mode but no couple yet — the invite is still pending. This is
  // the only persistent home for the code/link once the creation sheet closes
  // (whichever way it closes), so it must never be a dead end.
  if (!isLoading && !couple) {
    return <WaitingInviteCard t={t} getIdToken={getIdToken} refreshProfile={refreshProfile} />;
  }

  if (isLoading || !couple) return null;

  // Find the partner (not the current user — we don't know the id here, show both)
  const names = [couple.user1.profile?.displayName, couple.user2.profile?.displayName]
    .filter(Boolean).join(' & ');

  const partnerPhoto = couple.user1.profile?.avatarUrl || couple.user2.profile?.avatarUrl || null;
  const isDraft = couple.status === 'DRAFT';

  return (
    <div style={{ padding: '0 20px 14px' }}>
      <div style={{
        borderRadius: 20, overflow: 'hidden',
        border: `1.5px solid ${t.gold}55`,
        background: `linear-gradient(135deg, ${t.gold}12, ${t.blush}08)`,
        boxShadow: `0 2px 12px ${t.gold}18`,
      }}>
        <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14 }}>
          {/* Photo(s) */}
          <div style={{ position: 'relative', width: 56, flexShrink: 0 }}>
            {partnerPhoto ? (
              <Img placeholder={t.paperAlt} src={partnerPhoto} alt="" style={{ width: 56, height: 56, borderRadius: 14, objectFit: 'cover', border: `2px solid ${t.gold}44` }} />
            ) : (
              <div style={{ width: 56, height: 56, borderRadius: 14, background: `${t.gold}28`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontFamily: FONTS.display, fontSize: 22, color: t.gold }}>♥</span>
              </div>
            )}
            <div style={{ position: 'absolute', bottom: -4, right: -4, width: 16, height: 16, borderRadius: '50%', background: isDraft ? t.gold : t.sage, border: `2px solid ${t.paper}` }} />
          </div>

          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.12em', color: t.gold, marginBottom: 3 }}>
              {isDraft ? 'Setting up' : 'Together on Preminade'}
            </div>
            <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 20, color: t.ink, lineHeight: 1.2 }}>
              {names}
            </div>
            {couple.coupleBio && (
              <div style={{ fontFamily: FONTS.sans, fontSize: 12, color: t.inkSoft, marginTop: 4, lineHeight: 1.4 }}>
                "{couple.coupleBio}"
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div style={{ borderTop: `1px solid ${t.gold}28`, display: 'flex' }}>
          {isDraft && (
            <button onClick={handleActivate} style={{
              all: 'unset', cursor: 'pointer', flex: 1, padding: '12px 8px',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              fontFamily: FONTS.sans, fontSize: 12.5, fontWeight: 600, color: t.gold,
              borderRight: `1px solid ${t.gold}28`,
            }}>
              Go live
            </button>
          )}
          <button onClick={handleDissolve} disabled={dissolving} style={{
            all: 'unset', cursor: dissolving ? 'default' : 'pointer',
            flex: 1, padding: '12px 8px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            fontFamily: FONTS.sans, fontSize: 12.5, fontWeight: 500,
            color: dissolving ? t.inkMute : t.inkSoft,
          }}>
            {dissolving ? 'Leaving…' : 'Take a break'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Pair Up Card ────────────────────────────────────────────────
// Entry point for an existing SINGLES user to pair with their real-life
// partner. Reuses POST /couples/invite (link + code, no email needed) —
// after creation, the Discover waiting screen takes over.

function PairUpCard({ t, getIdToken, refreshProfile }: {
  t: Theme;
  getIdToken: () => Promise<string | null>;
  refreshProfile: () => Promise<void>;
}) {
  const queryClient = useQueryClient();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [invite, setInvite] = useState<{ code: string; link: string } | null>(null);
  const [copied, setCopied] = useState<'code' | 'link' | null>(null);

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    try {
      const token = await getIdToken();
      const res = await fetch(`${API_BASE}/api/couples/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({}),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Failed to create invite');
      setInvite({ code: body.code, link: body.link });
      // Mark caches stale so Me/Discover pick up the pending invite when they
      // next render. Deliberately NOT calling refreshProfile() here — that
      // would flip mode to COUPLES, unmount this card (it only renders for
      // SINGLES), and rip the sheet away while the user is reading the code.
      queryClient.invalidateQueries({ queryKey: ['couple-invite'] });
      queryClient.invalidateQueries({ queryKey: ['couple-me'] });
      queryClient.invalidateQueries({ queryKey: ['feed'] });
    } catch (e: any) {
      setError(e.message || 'Something went wrong — try again.');
    } finally {
      setCreating(false);
    }
  };

  const copy = async (value: string, which: 'code' | 'link') => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(which);
      setTimeout(() => setCopied(null), 1800);
    } catch { /* clipboard unavailable */ }
  };

  // Closing the sheet after an invite exists is the moment to sync the mode
  // flip (SINGLES → COUPLES). This card unmounts and the persistent
  // WaitingInviteCard takes over with the same code.
  const closeSheet = () => {
    setSheetOpen(false);
    if (invite) refreshProfile();
  };

  return (
    <div style={{ padding: '0 20px 14px' }}>
      <button
        onClick={() => setSheetOpen(true)}
        className="pm-press-soft"
        style={{
          all: 'unset', cursor: 'pointer', width: '100%', boxSizing: 'border-box',
          borderRadius: 18, padding: '14px 16px',
          border: `1.5px dashed ${t.gold}66`,
          background: `${t.gold}0A`,
          display: 'flex', alignItems: 'center', gap: 12,
        }}
      >
        <div style={{ width: 40, height: 40, borderRadius: 12, background: `${t.gold}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <span style={{ fontSize: 16, color: t.gold }}>♥♥</span>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: FONTS.sans, fontSize: 14, fontWeight: 600, color: t.ink }}>Here with someone?</div>
          <div style={{ fontFamily: FONTS.sans, fontSize: 12, color: t.inkMute, marginTop: 2, lineHeight: 1.4 }}>
            Pair up with your partner and go on double dates.
          </div>
        </div>
        <span style={{ fontFamily: FONTS.mono, fontSize: 12, color: t.gold }}>→</span>
      </button>

      {sheetOpen && (
        <div
          onClick={closeSheet}
          style={{
            position: 'fixed', inset: 0, zIndex: 300,
            background: 'rgba(10,8,6,0.55)',
            backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'flex-end',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 430, margin: '0 auto',
              background: t.paper, borderRadius: '24px 24px 0 0',
              paddingBottom: 'env(safe-area-inset-bottom)',
            }}
          >
            <div style={{ padding: '24px 22px 8px' }}>
              <div style={{ width: 40, height: 4, borderRadius: 2, background: t.line, margin: '0 auto 20px' }} />

              {!invite ? (
                <>
                  <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 26, color: t.ink, marginBottom: 10, lineHeight: 1.15 }}>
                    Pair up with your partner
                  </div>
                  <p style={{ fontFamily: FONTS.sans, fontSize: 14, color: t.inkSoft, lineHeight: 1.6, margin: '0 0 14px' }}>
                    You'll get a link and a code to share — your partner signs up with their own
                    profile and you're paired automatically. No email needed.
                  </p>
                  <p style={{ fontFamily: FONTS.sans, fontSize: 13, color: t.inkMute, lineHeight: 1.6, margin: '0 0 20px' }}>
                    This switches you to couples mode: you'll be hidden from singles discovery while
                    you wait, and your open singles matches close once you're paired. You can cancel
                    the invite anytime and go back to solo mode.
                  </p>
                  {error && (
                    <div style={{
                      margin: '0 0 16px', padding: '12px 14px', borderRadius: 12,
                      background: `${t.terracotta}12`, border: `1px solid ${t.terracotta}44`,
                      fontFamily: FONTS.sans, fontSize: 13, color: t.terracotta, lineHeight: 1.5,
                    }}>
                      {error}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 26, color: t.ink, marginBottom: 10, lineHeight: 1.15 }}>
                    Share this with your partner
                  </div>
                  <p style={{ fontFamily: FONTS.sans, fontSize: 14, color: t.inkSoft, lineHeight: 1.6, margin: '0 0 18px' }}>
                    They can open the link, or enter the code during sign-up. You'll find both again
                    in Discover while you wait.
                  </p>
                  <div
                    onClick={() => copy(invite.code, 'code')}
                    style={{
                      cursor: 'pointer', padding: '16px 24px', borderRadius: 16, textAlign: 'center',
                      background: t.card, border: `1px solid ${t.line}`, marginBottom: 12,
                      fontFamily: FONTS.mono, fontSize: 22, fontWeight: 700, letterSpacing: 4, color: t.ink,
                    }}
                  >
                    {invite.code}
                  </div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 8 }}>
                    <button onClick={() => copy(invite.code, 'code')} className="pm-press-soft" style={{
                      all: 'unset', cursor: 'pointer', padding: '9px 16px', borderRadius: 100,
                      background: t.paperAlt, border: `1px solid ${t.line}`,
                      fontFamily: FONTS.sans, fontSize: 12.5, fontWeight: 500, color: t.ink,
                    }}>
                      {copied === 'code' ? 'Copied ✓' : 'Copy code'}
                    </button>
                    <button onClick={() => copy(invite.link, 'link')} className="pm-press-soft" style={{
                      all: 'unset', cursor: 'pointer', padding: '9px 16px', borderRadius: 100,
                      background: t.paperAlt, border: `1px solid ${t.line}`,
                      fontFamily: FONTS.sans, fontSize: 12.5, fontWeight: 500, color: t.ink,
                    }}>
                      {copied === 'link' ? 'Copied ✓' : 'Copy invite link'}
                    </button>
                  </div>
                </>
              )}
            </div>

            <div style={{ padding: '8px 22px 28px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {!invite ? (
                <>
                  <button
                    onClick={handleCreate}
                    disabled={creating}
                    style={{
                      all: 'unset', cursor: creating ? 'default' : 'pointer', width: '100%', boxSizing: 'border-box',
                      padding: '15px', borderRadius: 14, textAlign: 'center',
                      fontFamily: FONTS.sans, fontSize: 15, fontWeight: 600,
                      background: t.gold, color: '#fff', opacity: creating ? 0.6 : 1,
                    }}
                  >
                    {creating ? 'Creating…' : 'Get my invite link'}
                  </button>
                  <button
                    onClick={() => setSheetOpen(false)}
                    disabled={creating}
                    style={{
                      all: 'unset', cursor: creating ? 'default' : 'pointer', width: '100%', textAlign: 'center',
                      padding: '10px', fontFamily: FONTS.sans, fontSize: 13, color: t.inkMute,
                    }}
                  >
                    Not now
                  </button>
                </>
              ) : (
                <button
                  onClick={closeSheet}
                  style={{
                    all: 'unset', cursor: 'pointer', width: '100%', boxSizing: 'border-box',
                    padding: '15px', borderRadius: 14, textAlign: 'center',
                    fontFamily: FONTS.sans, fontSize: 15, fontWeight: 600,
                    background: t.ink, color: t.paper,
                  }}
                >
                  Done
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// A label/value row. Pass onClick to make it editable (renders a real button
// with a chevron); pass readOnly for a value that is genuinely informational,
// which mutes it so it doesn't read as a setting someone can tap and change.
function EditableRow({ t, label, value, loading, onClick, pillColor, last, readOnly }: {
  t: Theme; label: string; value: string; loading?: boolean; onClick?: () => void; pillColor?: string; last?: boolean; readOnly?: boolean;
}) {
  const rowStyle = {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    width: '100%', boxSizing: 'border-box' as const, textAlign: 'left' as const,
    padding: '14px 16px', borderBottom: last ? 'none' : `1px solid ${t.line}`,
    fontFamily: FONTS.sans, fontSize: 14,
  };

  const content = (
    <>
      <span style={{ color: t.inkSoft }}>{label}</span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: readOnly ? t.inkMute : t.ink, fontWeight: 500 }}>
        {loading
          ? <span style={{ width: 14, height: 14, borderRadius: '50%', border: `2px solid ${t.terracotta}`, borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
          : value}
        {pillColor && <span style={{ width: 8, height: 8, borderRadius: 100, background: pillColor }} />}
        {onClick && !loading && Icon.arrow({ s: 14, c: t.inkMute })}
      </span>
    </>
  );

  // A row that does something on tap is a button — so it's reachable by
  // keyboard and announced as actionable, which a click-handling div is not.
  if (onClick) {
    return (
      <button onClick={onClick} className="pm-press-soft" style={{ all: 'unset', cursor: 'pointer', ...rowStyle }}>
        {content}
      </button>
    );
  }
  return <div style={rowStyle}>{content}</div>;
}

// ─── Feedback Button ─────────────────────────────────────────────

const FEEDBACK_CATEGORIES = [
  { key: 'bug',      label: 'Bug report' },
  { key: 'idea',     label: 'Idea'       },
  { key: 'feedback', label: 'Feedback'   },
  { key: 'love',     label: 'Love note'  },
];

function FeedbackButton({ t, userEmail }: { t: Theme; userEmail?: string }) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('feedback');
  const [message, setMessage] = useState('');

  const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  const handleSend = () => {
    const cat = FEEDBACK_CATEGORIES.find(c => c.key === category)!;
    const subject = encodeURIComponent(`[${cat.label}] Preminade feedback`);
    const body = encodeURIComponent(
      `${message}\n\n—\nSent from Preminade${userEmail ? `\n${userEmail}` : ''}`
    );
    window.location.href = `mailto:preminade@gmail.com?subject=${subject}&body=${body}`;
    setOpen(false);
    setMessage('');
  };

  // Ruled-line texture for the textarea — subtle horizontal lines like writing paper
  const ruledBg = `repeating-linear-gradient(
    to bottom,
    transparent,
    transparent 27px,
    ${t.line} 27px,
    ${t.line} 28px
  )`;

  return (
    <>
      {/* Trigger */}
      <button
        onClick={() => setOpen(true)}
        style={{
          all: 'unset', cursor: 'pointer', width: '100%', padding: '14px',
          borderRadius: 14, background: t.card, border: `1px solid ${t.line}`,
          textAlign: 'center', fontFamily: FONTS.sans, fontSize: 14,
          color: t.ink, fontWeight: 500, boxSizing: 'border-box',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        Send feedback
      </button>

      {/* Sheet */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 300,
            background: 'rgba(10,8,6,0.55)',
            backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'flex-end',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 430, margin: '0 auto',
              background: t.paper,
              borderRadius: '24px 24px 0 0',
              paddingBottom: 'env(safe-area-inset-bottom)',
              animation: 'slideUp 260ms cubic-bezier(0.32,0.72,0,1) both',
            }}
          >
            <style>{`@keyframes slideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}`}</style>

            {/* Letterhead */}
            <div style={{
              padding: '22px 22px 18px',
              borderBottom: `1px solid ${t.line}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
            }}>
              <div>
                {/* Brand mono caption */}
                <div style={{
                  fontFamily: FONTS.mono, fontSize: 9, letterSpacing: '0.14em',
                  textTransform: 'uppercase', color: t.inkMute, marginBottom: 6,
                }}>
                  Preminade · London
                </div>
                {/* Display headline */}
                <div style={{
                  fontFamily: FONTS.display, fontStyle: 'italic',
                  fontSize: 28, lineHeight: 1.1, color: t.ink,
                }}>
                  Tell us anything.
                </div>
                {/* Date line */}
                <div style={{
                  fontFamily: FONTS.mono, fontSize: 10, color: t.inkMute,
                  marginTop: 6, letterSpacing: '0.04em',
                }}>
                  {today}
                </div>
              </div>

              <button
                onClick={() => setOpen(false)}
                style={{
                  all: 'unset', cursor: 'pointer', marginTop: 2,
                  width: 30, height: 30, borderRadius: '50%',
                  background: t.paperAlt, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {Icon.x({ s: 14, c: t.inkMute })}
              </button>
            </div>

            {/* Category tabs — underline style */}
            <div style={{
              display: 'flex', gap: 0,
              borderBottom: `1px solid ${t.line}`,
              padding: '0 22px',
            }}>
              {FEEDBACK_CATEGORIES.map(c => (
                <button
                  key={c.key}
                  onClick={() => setCategory(c.key)}
                  style={{
                    all: 'unset', cursor: 'pointer',
                    paddingTop: 14, paddingBottom: 12,
                    paddingLeft: 0, paddingRight: 18,
                    fontFamily: FONTS.sans, fontSize: 12, fontWeight: 500,
                    color: category === c.key ? t.ink : t.inkMute,
                    borderBottom: category === c.key
                      ? `2px solid ${'#C0562A'}`  // terracotta
                      : '2px solid transparent',
                    marginBottom: -1,
                    transition: 'color 150ms, border-color 150ms',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {c.label}
                </button>
              ))}
            </div>

            {/* Composition area */}
            <div style={{ padding: '16px 22px 0' }}>

              {/* To: header */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                marginBottom: 12, paddingBottom: 10,
                borderBottom: `1px solid ${t.line}`,
              }}>
                <span style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: '0.1em', color: t.inkMute, textTransform: 'uppercase' }}>To</span>
                <span style={{ fontFamily: FONTS.sans, fontSize: 13, color: t.inkSoft }}>The Preminade team</span>
              </div>

              {/* Ruled textarea */}
              <div style={{ position: 'relative' }}>
                <div
                  aria-hidden
                  style={{
                    position: 'absolute', inset: 0,
                    background: ruledBg,
                    borderRadius: 4,
                    pointerEvents: 'none',
                    opacity: 0.6,
                  }}
                />
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder="Write your note here…"
                  rows={5}
                  style={{
                    all: 'unset', display: 'block',
                    width: '100%', boxSizing: 'border-box',
                    paddingTop: 4, paddingBottom: 4,
                    fontFamily: FONTS.sans, fontSize: 14, color: t.ink,
                    lineHeight: '28px',   // aligns with the 28px ruled-line repeat
                    resize: 'none',
                    position: 'relative', zIndex: 1,
                  }}
                />
              </div>

              {/* Signature line */}
              {userEmail && (
                <div style={{
                  marginTop: 10, paddingTop: 10,
                  borderTop: `1px solid ${t.line}`,
                  fontFamily: FONTS.mono, fontSize: 10,
                  color: t.inkMute, letterSpacing: '0.04em',
                }}>
                  — {userEmail}
                </div>
              )}
            </div>

            {/* Send button + footer */}
            <div style={{ padding: '16px 22px 32px' }}>
              <button
                onClick={handleSend}
                disabled={!message.trim()}
                style={{
                  all: 'unset',
                  cursor: message.trim() ? 'pointer' : 'default',
                  display: 'block', width: '100%', boxSizing: 'border-box',
                  padding: '14px', borderRadius: 14, textAlign: 'center',
                  fontFamily: FONTS.sans, fontSize: 14, fontWeight: 600,
                  letterSpacing: '0.01em',
                  background: message.trim() ? '#C0562A' : t.paperAlt,
                  color: message.trim() ? '#fff' : t.inkMute,
                  transition: 'background 180ms, color 180ms',
                }}
              >
                Send via email →
              </button>
              <div style={{
                marginTop: 10, textAlign: 'center',
                fontFamily: FONTS.mono, fontSize: 9,
                letterSpacing: '0.08em', color: t.inkMute,
                textTransform: 'uppercase',
              }}>
                Opens your mail app · preminade@gmail.com
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Re-renders when consent changes, so the label never goes stale.
function ConsentStatus({ t }: { t: Theme }) {
  const [consent, setConsentState] = useState(getConsent());
  useEffect(() => onConsentChange(() => setConsentState(getConsent())), []);
  return (
    <span data-testid="me-privacy-status" style={{ fontSize: 12.5, color: t.inkMute }}>
      Analytics {consent === 'granted' ? 'on' : 'off'}
    </span>
  );
}
